import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { AlertTriangle, Eye, MessageSquareText, Star, Users } from 'lucide-react'
import { env } from '@/lib/env'
import { cn } from '@/lib/cn'
import { formatDateTimeCompact } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { formatNumber, pluralize, truncate } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminRating, RatingFilters } from '@/types/api'
import { useTableState } from '@/hooks/useTableState'
import { DEFAULT_REPORT_RANGE, isReportRangeKey, rangeQuery, REPORT_RANGE_KEYS, REPORT_RANGES } from '@/features/reporting/api'
import { PageHeader } from '@/components/layout/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { fetchDriverRatingStats, fetchRatings, fetchRatingSummary } from './api'
import { describeJourney, formatAverage, isPositiveTag, moodLabel, summariseTags, TAG_OPTIONS, tagLabel } from './describe'
import { DriverLeaderboard } from './DriverLeaderboard'
import { RatingDetailsDrawer } from './RatingDetailsDrawer'
import { RatingDistribution } from './RatingDistribution'
import { RatingStars } from './RatingStars'
import styles from './RatingsPage.module.css'

/**
 * Thirty days by default, like Reporting: a month of trips is what "how are
 * we doing" means to an operations lead. The range lives in the URL with the
 * other filters, so a view can be bookmarked or sent to a colleague.
 */
const DEFAULT_FILTERS = {
  range: DEFAULT_REPORT_RANGE as string,
  search: '',
  band: '',
  rating: '',
  driverId: '',
  tag: '',
  hasComment: '',
}

const BAND_OPTIONS = [
  { value: 'HIGH', label: 'Good — 4 to 5 stars' },
  { value: 'MID', label: 'Okay — 3 stars' },
  { value: 'LOW', label: 'Needs attention — 1 to 2 stars' },
]

const STAR_OPTIONS = [5, 4, 3, 2, 1].map((stars) => ({
  value: String(stars),
  label: `${stars} ${stars === 1 ? 'star' : 'stars'} — ${moodLabel(stars)}`,
}))

const COMMENT_OPTIONS = [
  { value: 'true', label: 'With a comment' },
  { value: 'false', label: 'Score only' },
]

/**
 * What customers think of the drivers.
 *
 * The screen answers three questions in order, top to bottom: how are we doing
 * (the average and the response rate), where is it going wrong (the low
 * ratings and the reasons customers give), and who — the leaderboard, best
 * to worst. The list at the bottom is the evidence behind all three, and
 * every figure above it is computed over exactly the filter the list shows.
 *
 * Read-only by design. A rating is the customer's word; what the office does
 * about it happens outside this system.
 */
export function RatingsPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [selected, setSelected] = useState<AdminRating | null>(null)

  useEffect(() => {
    document.title = `Ratings · ${env.appName} Operations`
  }, [])

  const rangeKey = isReportRangeKey(table.filters.range) ? table.filters.range : DEFAULT_REPORT_RANGE
  const window = useMemo(() => rangeQuery(rangeKey), [rangeKey])

  const filters = useMemo<RatingFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      band: table.filters.band || undefined,
      rating: table.filters.rating ? Number(table.filters.rating) : undefined,
      driverId: table.filters.driverId ? Number(table.filters.driverId) : undefined,
      tag: table.filters.tag || undefined,
      hasComment: table.filters.hasComment === '' ? undefined : table.filters.hasComment === 'true',
      ...window,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters, window],
  )

  const ratingsQuery = useQuery({
    queryKey: queryKeys.ratings.list(filters),
    queryFn: () => fetchRatings(filters),
    placeholderData: (previous) => previous,
  })

  const summaryQuery = useQuery({
    queryKey: queryKeys.ratings.summary(filters),
    queryFn: () => fetchRatingSummary(filters),
    placeholderData: (previous) => previous,
  })

  // The leaderboard follows the range only, not the row filters: "who are our
  // best and worst drivers" is not a question about ratings with a comment.
  const driversQuery = useQuery({
    queryKey: queryKeys.ratings.drivers(window),
    queryFn: () => fetchDriverRatingStats(window),
    placeholderData: (previous) => previous,
  })

  const driverOptions = useMemo(
    () =>
      (driversQuery.data ?? []).map((driver) => ({
        value: String(driver.driverId),
        label: driver.driverName ?? `Driver #${driver.driverId}`,
      })),
    [driversQuery.data],
  )

  const columns = useMemo<ColumnDef<AdminRating, unknown>[]>(
    () => [
      {
        id: 'rating',
        header: 'Rating',
        meta: { sortKey: 'rating', mobileLabel: 'Rating', width: '150px' },
        cell: ({ row }) => (
          <div className={styles.ratingCell}>
            <RatingStars value={row.original.rating} size="sm" />
            <span className={cn(styles.mood, row.original.low && styles.moodLow)}>{moodLabel(row.original.rating)}</span>
          </div>
        ),
      },
      {
        id: 'driver',
        header: 'Driver',
        meta: { sortKey: 'driverName', mobileLabel: 'Driver' },
        cell: ({ row }) => (
          <div className={styles.person}>
            <Avatar name={row.original.driverName} src={row.original.driverImageUrl} size="sm" />
            <StackedCell
              primary={row.original.driverName ?? '—'}
              secondary={row.original.driverId != null ? `ID ${row.original.driverId}` : undefined}
            />
          </div>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        meta: { sortKey: 'customerName', mobileLabel: 'Customer', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell primary={row.original.customerName ?? '—'} secondary={row.original.customerPhone ?? undefined} />
        ),
      },
      {
        /* The reasons and the words together: they are what makes a score
           actionable, and a supervisor scanning the queue reads them before
           deciding which row to open. */
        id: 'feedback',
        header: 'Feedback',
        meta: { mobileLabel: 'Feedback' },
        cell: ({ row }) => (
          <div className={styles.feedbackCell}>
            {row.original.tags.length > 0 ? (
              <div className={styles.tagRow}>
                {row.original.tags.map((tag) => (
                  <span key={tag} className={cn(styles.tag, isPositiveTag(tag) ? styles.tagPositive : styles.tagNegative)}>
                    {tagLabel(tag)}
                  </span>
                ))}
              </div>
            ) : null}
            {row.original.comment ? (
              <p className={styles.commentPreview} title={row.original.comment}>
                “{truncate(row.original.comment, 90)}”
              </p>
            ) : row.original.tags.length === 0 ? (
              <span className={styles.muted}>Score only</span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'trip',
        header: 'Trip',
        meta: { sortKey: 'bookingReference', mobileLabel: 'Trip', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {row.original.bookingReference ?? (row.original.tripId != null ? `#${row.original.tripId}` : '—')}
              </span>
            }
            secondary={truncate(describeJourney(row.original), 56)}
          />
        ),
      },
      {
        id: 'createdAt',
        header: 'Rated',
        meta: { sortKey: 'createdAt', mobileLabel: 'Rated', hideOnMobile: true },
        cell: ({ row }) => formatDateTimeCompact(row.original.createdAt),
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '110px' },
        cell: ({ row }) => (
          <RowActions>
            <Button size="sm" variant="secondary" iconLeft={<Eye aria-hidden="true" />} onClick={() => setSelected(row.original)}>
              Details
            </Button>
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminRating>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Rated', value: (row) => formatDateTimeCompact(row.createdAt, '') },
      { header: 'Stars', value: (row) => row.rating },
      { header: 'Verdict', value: (row) => moodLabel(row.rating) },
      { header: 'Driver', value: (row) => row.driverName ?? '' },
      { header: 'Driver ID', value: (row) => row.driverId ?? '' },
      { header: 'Customer', value: (row) => row.customerName ?? '' },
      { header: 'Customer phone', value: (row) => row.customerPhone ?? '' },
      { header: 'Reasons', value: (row) => (row.tags.length ? summariseTags(row.tags) : '') },
      { header: 'Comment', value: (row) => row.comment ?? '' },
      { header: 'Booking', value: (row) => row.bookingReference ?? '' },
      { header: 'Pickup', value: (row) => row.pickupAddress ?? '' },
      { header: 'Destination', value: (row) => row.dropoffAddress ?? '' },
      { header: 'Package', value: (row) => row.packageName ?? '' },
      { header: 'Trip start', value: (row) => formatDateTimeCompact(row.tripStartTime, '') },
      { header: 'Trip ended', value: (row) => formatDateTimeCompact(row.tripEndedAt, '') },
      { header: 'Trip price', value: (row) => row.tripTotalPrice ?? '' },
    ],
    [],
  )

  const page = ratingsQuery.data
  const summary = summaryQuery.data
  const responseRate = summary?.responseRate

  return (
    <>
      <PageHeader
        title="Ratings"
        description="What customers said about their drivers once the trip was over. Every figure on this page is over the ratings the filters select — change the filters and the numbers follow."
        meta={
          summary ? (
            <>
              <span>
                {formatNumber(summary.ratingCount)} {pluralize(summary.ratingCount, 'rating')}
              </span>
              <span>
                {summary.completedTripCount > 0
                  ? `${formatNumber(summary.completedTripCount)} ${pluralize(summary.completedTripCount, 'finished trip')} in ${REPORT_RANGES[rangeKey].label.toLowerCase()}`
                  : REPORT_RANGES[rangeKey].label}
              </span>
            </>
          ) : null
        }
        actions={
          <div className={styles.rangeControl} role="group" aria-label="Period">
            {REPORT_RANGE_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={cn(styles.rangeButton, key === rangeKey && styles.rangeButtonActive)}
                aria-pressed={key === rangeKey}
                title={REPORT_RANGES[key].label}
                onClick={() => table.setFilter('range', key)}
              >
                {REPORT_RANGES[key].shortLabel}
              </button>
            ))}
          </div>
        }
      />

      {/* ------------------------------------------------- headline figures */}
      <div className={styles.kpiGrid}>
        <div className={cn(styles.statCard, styles.statCardHero)}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Average rating</span>
            <span className={styles.statIcon} data-tone="amber">
              <Star aria-hidden="true" />
            </span>
          </div>
          <p className={styles.statValue}>{summary ? formatAverage(summary.average) : '—'}</p>
          <div className={styles.statStars}>
            {summary?.average != null ? (
              <RatingStars value={Math.round(summary.average)} size="md" withLabel />
            ) : (
              <span className={styles.statHint}>No ratings in this period yet</span>
            )}
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Response rate</span>
            <span className={styles.statIcon} data-tone="info">
              <Users aria-hidden="true" />
            </span>
          </div>
          <p className={styles.statValue}>{responseRate != null ? `${Math.round(responseRate * 100)}%` : '—'}</p>
          <p className={styles.statHint}>
            {summary
              ? summary.completedTripCount > 0
                ? `${formatNumber(summary.ratingCount)} of ${formatNumber(summary.completedTripCount)} finished trips were rated`
                : 'No trips finished in this period'
              : ' '}
          </p>
        </div>

        <div className={cn(styles.statCard, summary && summary.lowCount > 0 && styles.statCardAlert)}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Needs attention</span>
            <span className={styles.statIcon} data-tone={summary && summary.lowCount > 0 ? 'danger' : undefined}>
              <AlertTriangle aria-hidden="true" />
            </span>
          </div>
          <p className={styles.statValue}>{summary ? formatNumber(summary.lowCount) : '—'}</p>
          <p className={styles.statHint}>
            {summary && summary.lowCount > 0 ? (
              <button type="button" className={styles.statLink} onClick={() => table.setFilter('band', 'LOW')}>
                Show the 1–2 star ratings
              </button>
            ) : (
              'Ratings of 1 or 2 stars'
            )}
          </p>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>With a comment</span>
            <span className={styles.statIcon}>
              <MessageSquareText aria-hidden="true" />
            </span>
          </div>
          <p className={styles.statValue}>{summary ? formatNumber(summary.withCommentCount) : '—'}</p>
          <p className={styles.statHint}>
            {summary && summary.withCommentCount > 0 ? (
              <button type="button" className={styles.statLink} onClick={() => table.setFilter('hasComment', 'true')}>
                Read what customers wrote
              </button>
            ) : (
              'Customers who added words to the score'
            )}
          </p>
        </div>
      </div>

      {/* ------------------------------------------------- the shape of it */}
      <div className={styles.grid}>
        <Card>
          <CardHeader title="By score" description="Click a row to see just those ratings." />
          <CardBody>
            <RatingDistribution
              distribution={summary?.distribution}
              isLoading={summaryQuery.isLoading}
              selected={table.filters.rating}
              onSelect={(stars) => table.setFilter('rating', table.filters.rating === stars ? '' : stars)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="What customers mention" description="The reasons ticked with a score, most common first." />
          <CardBody>
            {summaryQuery.isLoading ? (
              <p className={styles.muted}>Loading…</p>
            ) : summary && summary.tags.length > 0 ? (
              <ul className={styles.tagCloud} role="list">
                {summary.tags.map((entry) => (
                  <li key={entry.tag}>
                    <button
                      type="button"
                      className={cn(
                        styles.tagCloudChip,
                        entry.positive ? styles.tagPositive : styles.tagNegative,
                        table.filters.tag === entry.tag && styles.tagCloudChipActive,
                      )}
                      aria-pressed={table.filters.tag === entry.tag}
                      onClick={() => table.setFilter('tag', table.filters.tag === entry.tag ? '' : entry.tag)}
                    >
                      {tagLabel(entry.tag)}
                      <span className={styles.tagCloudCount}>{formatNumber(entry.count)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.muted}>No reasons have been ticked in this period.</p>
            )}
          </CardBody>
        </Card>

        <Card className={styles.wide}>
          <CardHeader
            title="Drivers"
            description="Every driver rated in this period, best average first. Click a driver to see their ratings."
          />
          <DriverLeaderboard
            drivers={driversQuery.data}
            isLoading={driversQuery.isLoading}
            error={driversQuery.isError ? driversQuery.error : undefined}
            onRetry={() => void driversQuery.refetch()}
            selectedDriverId={table.filters.driverId}
            onSelect={(driverId) => table.setFilter('driverId', table.filters.driverId === driverId ? '' : driverId)}
          />
        </Card>
      </div>

      {/* ------------------------------------------------------- the list */}
      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search booking, customer, driver or comment…"
              label="Search ratings"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-ratings"
              title="Drive For Me — Customer ratings"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) => fetchRatings({ ...filters, page: exportPage, size: exportSize }))
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Verdict"
            value={table.filters.band}
            onChange={(value) => table.setFilter('band', value)}
            options={BAND_OPTIONS}
          />
          <FilterSelect
            label="Stars"
            value={table.filters.rating}
            onChange={(value) => table.setFilter('rating', value)}
            options={STAR_OPTIONS}
          />
          <FilterSelect
            label="Reason"
            value={table.filters.tag}
            onChange={(value) => table.setFilter('tag', value)}
            options={TAG_OPTIONS}
          />
          <FilterSelect
            label="Comment"
            value={table.filters.hasComment}
            onChange={(value) => table.setFilter('hasComment', value)}
            options={COMMENT_OPTIONS}
          />
          <FilterSelect
            label="Driver"
            value={table.filters.driverId}
            onChange={(value) => table.setFilter('driverId', value)}
            options={driverOptions}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me customer ratings"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={ratingsQuery.isLoading}
          isFetching={ratingsQuery.isFetching && !ratingsQuery.isLoading}
          error={ratingsQuery.isError ? ratingsQuery.error : undefined}
          onRetry={() => void ratingsQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={table.activeFilterCount > 0 ? 'No ratings match these filters' : 'No ratings yet'}
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try a different verdict, reason or period, or clear the filters.'
              : 'A rating appears here the moment a customer gives one at the end of a trip.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="rating"
            pluralNoun="ratings"
            disabled={ratingsQuery.isFetching}
          />
        ) : null}
      </Card>

      <RatingDetailsDrawer rating={selected} onClose={() => setSelected(null)} />
    </>
  )
}
